import { Icon } from '~/atomic/atoms/Icon/view';
import { Text } from '~/atomic/atoms/Text/view';
import { useAppSelector } from '~/store';
import {
  selectPlanLabel,
  selectPlanSlug,
  selectSubscription,
} from '~/store/entitlements/selectors';

interface CurrentPlanCardProps {
  onClickUpgrade?: () => void;
}

const PLAN_LABEL: Record<string, string> = {
  free: 'Free',
  freemium: 'Freemium',
  premium_basic: 'Premium Basic',
  premium_pro: 'Premium Pro',
  premium_enterprise: 'Premium Enterprise',
};

const PLAN_TAGLINE: Record<string, string> = {
  free: 'Cadastre contratos e acompanhe parcelas',
  freemium: 'Recursos ampliados com limites mensais',
  premium_basic: 'Tudo o que você precisa pra acompanhar a safra',
  premium_pro: 'Ferramentas avançadas e múltiplas fazendas',
  premium_enterprise: 'Integrações bancárias e relatórios corporativos',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'Ativo',
  trialing: 'Em trial',
  past_due: 'Pagamento pendente',
  cancelled: 'Cancelado',
  expired: 'Expirado',
  manual_grant: 'Concedido pela equipe',
};

function formatRenewsAt(iso: string): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
    .format(d)
    .replace('.', '');
}

export default function CurrentPlanCard({ onClickUpgrade }: CurrentPlanCardProps) {
  const planSlug = useAppSelector(selectPlanSlug);
  const planLabel = useAppSelector(selectPlanLabel);
  const subscription = useAppSelector(selectSubscription);

  const label = planLabel || PLAN_LABEL[planSlug] || planSlug;
  const tagline = PLAN_TAGLINE[planSlug] ?? 'Seu plano atual.';
  const isFree = planSlug === 'free' && !subscription;
  const ctaText = isFree ? 'Conhecer planos' : 'Gerenciar plano';

  const statusLabel = subscription
    ? STATUS_LABEL[subscription.status] ?? subscription.status
    : null;

  return (
    <article className="relative overflow-hidden rounded-medium border border-support-01 bg-intense-01 shadow-level1">
      <div className="flex h-[6px]">
        <div className="w-1/2 bg-primary-01" />
        <div className="w-1/4 bg-secondary-01" />
        <div className="w-1/4 bg-support-01" />
      </div>

      <div className="flex flex-col gap-smaller p-medium">
        <div className="flex items-center justify-between gap-quark">
          <span className="text-captionLG uppercase tracking-wider color-intense-05">
            Plano atual
          </span>
          {statusLabel ? (
            <span className="inline-flex items-center rounded-full bg-support-01 px-nano py-quark text-[10px] font-bold uppercase tracking-[0.12em] color-primary-01">
              {statusLabel}
            </span>
          ) : null}
        </div>

        <div className="flex items-baseline gap-quark">
          <Text className="text-display1 color-primary-01">{label}</Text>
        </div>

        <Text className="text-bodySM color-intense-06">{tagline}</Text>

        {subscription?.renewsAt ? (
          <div className="flex items-center gap-quark text-caption color-intense-06">
            <Icon name="icon-calendar" size={12} color="#606060" rounded={false} />
            <span>Renova em {formatRenewsAt(subscription.renewsAt)}</span>
          </div>
        ) : null}

        {subscription?.status === 'trialing' &&
        subscription?.trialEndsAt &&
        new Date(subscription.trialEndsAt) > new Date() ? (
          <div className="flex items-center gap-quark text-caption color-alert-06">
            <Icon name="icon-clock" size={12} color="#99701F" rounded={false} />
            <span>Trial termina em {formatRenewsAt(subscription.trialEndsAt)}</span>
          </div>
        ) : null}

        {isFree ? (
          <ul className="mt-quark flex flex-col gap-quark text-bodySM color-intense-07">
            <li className="flex items-center gap-nano">
              <span className="flex h-[16px] w-[16px] items-center justify-center rounded-full bg-support-01">
                <Icon name="icon-check" size={10} color="#1D4733" rounded={false} />
              </span>
              Extração de PDF, alertas e previsão liberados no Premium
            </li>
            <li className="flex items-center gap-nano">
              <span className="flex h-[16px] w-[16px] items-center justify-center rounded-full bg-support-01">
                <Icon name="icon-check" size={10} color="#1D4733" rounded={false} />
              </span>
              Múltiplas fazendas e relatórios avançados
            </li>
          </ul>
        ) : null}

        <button
          type="button"
          onClick={onClickUpgrade}
          className="mt-smaller inline-flex w-fit items-center gap-quark rounded-nano border border-primary-01 px-small py-quark text-bodyMD font-medium color-primary-01 transition-colors hover:bg-support-01 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-01/30"
        >
          {ctaText}
          <Icon name="icon-chevron-right" size={14} color="#1D4733" rounded={false} />
        </button>
      </div>
    </article>
  );
}
